import { cn } from "@devhop/ui";
import { BadgePercentIcon, BanIcon, CoinsIcon } from "lucide-react";

export type SellPackageDiscountType = "none" | "percentage" | "fixed";

type SellPackageDiscountTypeRadioProps = {
  value: SellPackageDiscountType | undefined;
  onChange: (value: SellPackageDiscountType) => void;
  onBlur?: () => void;
  name?: string;
  disabled?: boolean;
  label?: string;
};

const OPTIONS: Array<{
  value: SellPackageDiscountType;
  title: string;
  description: string;
  Icon: typeof BanIcon;
}> = [
  {
    value: "none",
    title: "ບໍ່ມີສ່ວນຫຼຸດ",
    description: "ລາຄາຕາມແມ່ແບບເຕັມ",
    Icon: BanIcon,
  },
  {
    value: "percentage",
    title: "ເປີເຊັນ (%)",
    description: "ຫຼຸດເປີເຊັນຈາກຍອດເງິນ",
    Icon: BadgePercentIcon,
  },
  {
    value: "fixed",
    title: "ຈຳນວນເງິນຄົງທີ",
    description: "ຫຼຸດຄົງທີເປັນກີບ",
    Icon: CoinsIcon,
  },
];

export function SellPackageDiscountTypeRadio({
  value,
  onChange,
  onBlur,
  name = "discountType",
  disabled = false,
  label = "ສ່ວນຫຼຸດ (ບໍ່ບັງຄັບ)",
}: SellPackageDiscountTypeRadioProps) {
  const selected = value ?? "none";

  return (
    <fieldset className="min-w-0 border-0 p-0">
      <legend className="mb-2 font-medium text-sm">{label}</legend>
      <div className="grid gap-3 sm:grid-cols-3">
        {OPTIONS.map(({ value: optValue, title, description, Icon }) => {
          const isSelected = selected === optValue;
          return (
            <label
              key={optValue}
              className={cn(
                "flex cursor-pointer gap-3 rounded-lg border p-4 text-left ring-offset-background transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-red-600 focus-within:ring-offset-2",
                disabled && "cursor-not-allowed opacity-60",
                isSelected
                  ? "border-red-600 bg-muted/50"
                  : "border-border hover:bg-muted/30",
              )}
            >
              <input
                type="radio"
                name={name}
                value={optValue}
                checked={isSelected}
                disabled={disabled}
                onChange={() => onChange(optValue)}
                onBlur={onBlur}
                className="sr-only"
              />
              <Icon
                className={cn(
                  "mt-0.5 size-5 shrink-0 stroke-[1.5]",
                  isSelected ? "text-red-600" : "text-muted-foreground",
                )}
                aria-hidden
              />
              <span className="min-w-0 space-y-1">
                <span className="block font-semibold text-sm leading-tight">
                  {title}
                </span>
                <span className="block text-muted-foreground text-xs leading-snug">
                  {description}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
